import React from 'react'
import { getPlatformTypeForHost } from '@reward-platform/platform-config'
import { createDatadogMetaContext } from '@reward-platform/logger/utils'
import { createDatadogLogger } from '~/utils/logger'
import { datadogRum } from '~/utils/datadog'
import { GenericClientError } from '~/utils/errors'
import { isClient } from '~/utils/envChecks'
import ErrorBoundaryPage from './ErrorBoundaryPage'
import { ErrorBoundaryProps } from './ErrorBoundary.types'

const getPlatformType = () => {
  if (isClient()) {
    const { host } = window.location
    return getPlatformTypeForHost(host)
  }
  return 'avios'
}

interface State {
  error: Error | null
}

type Props = React.PropsWithChildren<ErrorBoundaryProps>

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    // Define a state variable to track whether is an error or not
    this.state = { error: null }
  }

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { error }
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const renderingError = new Error(error.message)
    renderingError.name = `ReactRenderingError`
    renderingError.stack = errorInfo.componentStack ?? undefined
    renderingError.cause = error
    datadogRum.addError(renderingError)

    const { opCo } = this.props

    const meta = { partner: opCo, platformType: getPlatformType() }
    if (error instanceof GenericClientError) {
      error.logToDatadog(meta)
    } else {
      const context = {
        ...errorInfo,
        ...createDatadogMetaContext(meta),
      }
      const logger = createDatadogLogger('react-error-boundary')
      logger.error(error.message, context, renderingError)
    }
  }

  private reset() {
    this.setState({ error: null })
  }

  public render() {
    const { error } = this.state
    const { children } = this.props

    if (error) {
      return <ErrorBoundaryPage {...this.props} error={error} reset={this.reset} />
    }

    return children
  }
}

export default ErrorBoundary
